import { useState } from 'react';
import {
  Folder,
  FolderOpen,
  ChevronRight,
  ChevronDown,
  MoreVertical,
  Plus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { AutomationFolder, Automation } from '@/types/automation';

interface FolderTreeProps {
  folders: AutomationFolder[];
  selectedFolderId?: string | null;
  onSelectFolder: (folderId: string | null) => void;
  onCreateFolder: () => void;
  onRenameFolder: (folderId: string) => void;
  onDeleteFolder: (folderId: string) => void;
  uncategorizedCount: number;
  automations?: Automation[];
  onEditAutomation?: (automationId: string) => void;
  onDragOver?: (e: React.DragEvent, folderId: string | null) => void;
  onDrop?: (e: React.DragEvent, folderId: string | null) => void;
}

export function FolderTree({
  folders,
  selectedFolderId,
  onSelectFolder,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  uncategorizedCount,
  automations = [],
  onEditAutomation,
  onDragOver,
  onDrop
}: FolderTreeProps) {
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());

  const toggleFolder = (folderId: string) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  const handleDragOver = (e: React.DragEvent, folderId: string | null) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDragOver) {
      onDragOver(e, folderId);
    }
  };

  const handleDrop = (e: React.DragEvent, folderId: string | null) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDrop) {
      onDrop(e, folderId);
    }
  };

  // Sort folders by display_order
  const sortedFolders = [...folders].sort((a, b) => a.display_order - b.display_order);

  return (
    <div className="w-64 border-r bg-muted/10 p-4 flex flex-col gap-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-sm">Folders</h3>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={onCreateFolder}
          title="Create Folder"
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>

      {/* Automations (root level) */}
      <div
        className={cn(
          "flex items-center justify-between gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors hover:bg-muted",
          selectedFolderId === null && "bg-primary text-primary-foreground"
        )}
        onClick={() => onSelectFolder(null)}
        onDragOver={(e) => handleDragOver(e, null)}
        onDrop={(e) => handleDrop(e, null)}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Folder className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
          <span className="text-sm truncate font-semibold">Automations</span>
        </div>
        {uncategorizedCount > 0 && (
          <span className="text-xs text-muted-foreground">({uncategorizedCount})</span>
        )}
      </div>

      {/* Folder List */}
      <div className="flex flex-col gap-1">
        {sortedFolders.map((folder) => {
          const isExpanded = expandedFolders.has(folder.id);
          const isSelected = selectedFolderId === folder.id;

          return (
            <div
              key={folder.id}
              className="group"
            >
              <div
                className={cn(
                  "flex items-center justify-between gap-1 px-2 py-1.5 rounded-md transition-colors hover:bg-muted",
                  isSelected && "bg-primary text-primary-foreground"
                )}
                onDragOver={(e) => handleDragOver(e, folder.id)}
                onDrop={(e) => handleDrop(e, folder.id)}
              >
                {/* Left: Chevron + Folder Icon + Name + Count */}
                <div
                  className="flex items-center gap-1 flex-1 min-w-0 cursor-pointer"
                  onClick={() => onSelectFolder(folder.id)}
                >
                  {/* Chevron (expand/collapse) */}
                  <button
                    className="flex-shrink-0 p-0.5 hover:bg-accent rounded"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFolder(folder.id);
                    }}
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-3 h-3" />
                    ) : (
                      <ChevronRight className="w-3 h-3" />
                    )}
                  </button>

                  {/* Folder Icon */}
                  {isExpanded ? (
                    <FolderOpen
                      className="w-4 h-4 flex-shrink-0"
                      style={{ color: folder.color || undefined }}
                    />
                  ) : (
                    <Folder
                      className="w-4 h-4 flex-shrink-0"
                      style={{ color: folder.color || undefined }}
                    />
                  )}

                  {/* Name */}
                  <span className="text-sm truncate" title={folder.name}>
                    {folder.name}
                  </span>

                  {/* Count */}
                  {(folder.automation_count ?? 0) > 0 && (
                    <span className="text-xs text-muted-foreground">
                      ({folder.automation_count})
                    </span>
                  )}
                </div>

                {/* Right: More Actions */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreVertical className="w-3 h-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onRenameFolder(folder.id)}>
                      Rename
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => onDeleteFolder(folder.id)}
                      className="text-destructive focus:text-destructive"
                      disabled={(folder.automation_count ?? 0) > 0}
                    >
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Expanded Content - Show Automations in Folder */}
              {isExpanded && (
                <div className="ml-6 mt-1 space-y-0.5">
                  {automations
                    .filter(a => a.folder_id === folder.id)
                    .map(automation => (
                      <div
                        key={automation.id}
                        className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-accent/50 text-xs text-muted-foreground cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onEditAutomation && automation.id) {
                            onEditAutomation(automation.id);
                          }
                        }}
                        title={automation.name}
                      >
                        <div className="w-1 h-1 rounded-full bg-muted-foreground/50 flex-shrink-0" />
                        <span className="truncate">{automation.name}</span>
                      </div>
                    ))}
                  {automations.filter(a => a.folder_id === folder.id).length === 0 && (
                    <div className="text-xs text-muted-foreground/50 italic px-2 py-1">
                      No automations
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {sortedFolders.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <Folder className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-xs">No folders yet</p>
          <p className="text-xs">Click + to create one</p>
        </div>
      )}
    </div>
  );
}
